import { type FC, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Formik, type FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  type UiHStackProps,
  UiStack,
} from '@/lib/ui';
import BaseFormDrawer from '@/base/Form/Drawer';
import BaseFormFieldGroup from '@/base/Form/FieldGroup';
import BaseFormInputField from '@/base/Form/InputField';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { type TempRegistration, type TempRegistrationSaveRequest, cartQueryKey, saveTempRegistration, tempRegistrationsQueryKey } from '@/api/registration';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { groupRegistrationQueryKey } from '@/api/registration/groupRegistration';
import { useUserSession } from '@/registration/hook/useUserSession';
import { EMAIL_REGEX } from '@/lib/util/regex';
import { useBreakpointValue } from '@chakra-ui/react';
import { type Layout } from '@/base/Form/FieldContainer';

export interface AttendeesFormProps extends UiHStackProps {
  defaultAttendee?: TempRegistration
  onClose: () => void
  onSaveSuccess: () => void
  isVisible: boolean
  groupRegistrationId: number
}

interface FormData {
  // attendees: {[key: number]: string[]}
  email: string
  firstName: string
  lastName: string
}

const formSchema = Yup.object().shape({
  email: Yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Email is required'),
  firstName: Yup.string().required('First name is required.'),
  lastName: Yup.string().required('Last name is required.'),
});

const AttendeeForm: FC<AttendeesFormProps> = ({
  defaultAttendee,
  isVisible,
  onClose,
  groupRegistrationId,
}) => {
  const layoutInput: Layout | undefined = useBreakpointValue(
    {
      base: 'stack',
      sm: 'inline',

    }, { fallback: 'sm' });

  const { eventId } = useRegisterRoute();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const { createTenantApiRequest } = useTenantApi();
  const { userSession } = useUserSession();
  const queryClient = useQueryClient();

  const { mutate: createMutate, isLoading: isCreateMutationLoading } = useMutation<ApiResponseSingle<TempRegistration>, Error, TempRegistrationSaveRequest>({
    mutationFn: async (params: TempRegistrationSaveRequest) => {
      return await saveTempRegistration(createTenantApiRequest)(params);
    },
    onSuccess: (response: ApiResponseSingle<TempRegistration>) => {
      if (response.errors) {
        setSaveErrors(response.errors);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: [tempRegistrationsQueryKey, { eventId, groupRegistrationId }] });
      void queryClient.invalidateQueries({ queryKey: [groupRegistrationQueryKey, { eventId }, { token: userSession?.authToken }] });
      if (defaultAttendee?.id) {
        void queryClient.invalidateQueries({ queryKey: [cartQueryKey] });
      }
      // NOTE: Reset form values
      onClose();
      setSaveErrors([]);
    },
    onError: () => {
      // NOTE: handle error
    }
  });

  const submitForm = async (values: FormData) => {
    createMutate({ temporaryRegistration: { ...values, groupRegistrationId, id: defaultAttendee?.id }, eventId });
  };

  // NOTE: Reset save errors when the modal opened by click add attendee
  useEffect(() => {
    if (isVisible) {
      setSaveErrors([]);
    }
  }, [isVisible]);

  return (
    <Formik<FormData>
      initialValues={{
        email: defaultAttendee?.email ?? '',
        firstName: defaultAttendee?.firstName ?? '',
        lastName: defaultAttendee?.lastName ?? '',
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={formSchema}
      onSubmit={async (values, { setSubmitting, resetForm }: FormikHelpers<FormData>) => {
        setSubmitting(true);
        try {
          await submitForm(values);
          resetForm();
        } catch (error) {
          // Handle submit error
        }
        setSubmitting(false);
      }}
    >
      <BaseFormDrawer
        isOpen={isVisible}
        onClose={onClose}
        title={defaultAttendee?.id ? 'Edit attendee' : 'Add attendee'}
        size={'lg'}
        isLoading={isCreateMutationLoading}
      >
        {saveErrors.length > 0 && (
          <UiStack spacing={4} flexGrow={1} py={4}>
            {saveErrors.map((error, index) => {
              return (
                <BaseMessageBarError key={index}>{error}</BaseMessageBarError>
              );
            })}
          </UiStack>
        )}
        <BaseFormFieldGroup>
          <BaseFormInputField
            name={'email'}
            label="Email"
            layout={layoutInput}
          />
          <BaseFormInputField
            name={'firstName'}
            label="First name"
            layout={layoutInput}
          />
          <BaseFormInputField
            name={'lastName'}
            label="Last name"
            layout={layoutInput}
          />
        </BaseFormFieldGroup>
      </BaseFormDrawer>
    </Formik>
  );
};

export default AttendeeForm;
